import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { ProfilePictureUpload } from '../../components/ProfilePictureUpload';
import {
  getAccount,
  updateAccount,
  deleteAccount,
} from '../../services/accountService';
import type { AccountProfile } from '../../shared/account-types';
import '../../styles/profile/ProfilePage.css';

/**
 * ProfilePage - User profile management
 */
export const ProfilePage: React.FC = () => {
  const { authStatus, requestVerificationEmail, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const user = authStatus?.user;

  // Profile state loaded from /api/account
  const [accountProfile, setAccountProfile] = useState<AccountProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  // Verification email state
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [isResendLoading, setIsResendLoading] = useState(false);

  // Edit form state (directly editable)
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', email: '' });
  const [originalValues, setOriginalValues] = useState({ first_name: '', last_name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Delete account modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Success message
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load account profile on mount
  useEffect(() => {
    let mounted = true;

    async function loadProfile() {
      setIsLoadingProfile(true);

      try {
        const response = await getAccount();

        if (!mounted) return;

        if (response.success && response.data) {
          setAccountProfile(response.data);
          const values = {
            first_name: response.data.first_name || '',
            last_name: response.data.last_name || '',
            email: response.data.email || '',
          };
          setEditForm(values);
          setOriginalValues(values);
        } else {
          console.warn('Could not load account profile:', response.error);
          // Fallback to auth context
          if (mounted && user) {
            const values = {
              first_name: user.first_name || '',
              last_name: user.last_name || '',
              email: user.email || '',
            };
            setEditForm(values);
            setOriginalValues(values);
          }
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
        // Fallback to auth context
        if (mounted && user) {
          const values = {
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email: user.email || '',
          };
          setEditForm(values);
          setOriginalValues(values);
        }
      } finally {
        if (mounted) {
          setIsLoadingProfile(false);
        }
      }
    }

    loadProfile();

    return () => {
      mounted = false;
    };
  }, [user]);

  // Auto-dismiss success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const getColorFromEmail = (email: string): string => {
    const colors = [
      '#0078d4', '#107c10', '#d83b01', '#8764b8',
      '#00b4ff', '#e81123', '#008272',
    ];
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      hash = email.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Computed values
  const profile = accountProfile;
  const email = profile?.email || user?.email || '';
  const firstName = profile?.first_name || user?.first_name || '';
  const lastName = profile?.last_name || user?.last_name || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const initial = (firstName?.charAt(0) || email.charAt(0)).toUpperCase();
  const displayName = fullName || email.split('@')[0];
  const backgroundColor = getColorFromEmail(email);
  const isOAuthUser = profile?.is_oauth_user ?? false;
  const canChangeEmail = profile?.can_change_email ?? !isOAuthUser;
  const company = profile?.company || user?.company || '';
  const isVerified = profile?.is_verified ?? user?.is_verified ?? false;
  const isAdmin = profile?.is_admin ?? user?.is_admin ?? false;
  const profilePictureUrl = profile?.profile_picture_url || null;

  // Check if form has changes
  const hasChanges = editForm.first_name !== originalValues.first_name ||
    editForm.last_name !== originalValues.last_name ||
    (canChangeEmail && editForm.email !== originalValues.email);

  const handleResendVerification = async () => {
    setIsResendLoading(true);
    setVerificationSent(false);
    setVerificationError('');

    try {
      const result = await requestVerificationEmail(email);

      if (result.success) {
        setVerificationSent(true);
      } else {
        setVerificationError(result.error || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      console.error('Failed to send verification email:', err);
      setVerificationSent(false);
      setVerificationError(err instanceof Error ? err.message : 'Erreur lors de l\'envoi de l\'email');
    } finally {
      setIsResendLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Save changes
  const handleSaveChanges = useCallback(async () => {
    const updateData: { first_name?: string; last_name?: string; email?: string } = {};

    if (editForm.first_name !== originalValues.first_name) {
      updateData.first_name = editForm.first_name;
    }
    if (editForm.last_name !== originalValues.last_name) {
      updateData.last_name = editForm.last_name;
    }
    if (canChangeEmail && editForm.email !== originalValues.email) {
      updateData.email = editForm.email;
    }

    if (Object.keys(updateData).length === 0) {
      return;
    }

    setIsSubmitting(true);
    setEditError(null);

    try {
      const response = await updateAccount(updateData);

      if (response.success && response.data) {
        setAccountProfile(response.data);
        const newValues = {
          first_name: response.data.first_name || '',
          last_name: response.data.last_name || '',
          email: response.data.email || '',
        };
        setEditForm(newValues);
        setOriginalValues(newValues);
        await refreshUser();
        setSuccessMessage('Profil mis à jour avec succès');
      } else {
        if (response.code === 'OAUTH_USER_CANNOT_CHANGE_EMAIL') {
          setEditError('Les utilisateurs connectés via Google ou Microsoft ne peuvent pas modifier leur email.');
        } else if (response.code === 'EMAIL_ALREADY_IN_USE' || response.code === 'HTTP_409') {
          setEditError('Cette adresse email est déjà utilisée par un autre compte.');
        } else {
          setEditError(response.error || 'Erreur lors de la mise à jour');
        }
      }
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour');
    } finally {
      setIsSubmitting(false);
    }
  }, [editForm, originalValues, canChangeEmail, refreshUser]);

  // Cancel changes
  const handleCancelChanges = useCallback(() => {
    setEditForm(originalValues);
    setEditError(null);
  }, [originalValues]);

  // Delete account handlers
  const openDeleteModal = useCallback(() => {
    setDeleteError(null);
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeleteError(null);
  }, []);

  const handleDeleteAccount = useCallback(async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      const response = await deleteAccount();

      if (response.success) {
        await logout();
        navigate('/login');
      } else {
        setDeleteError(response.error || 'Erreur lors de la suppression du compte');
      }
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Erreur lors de la suppression du compte');
    } finally {
      setIsDeleting(false);
    }
  }, [logout, navigate]);

  // Profile picture update handler
  const handlePictureUpdated = useCallback((newUrl: string | null) => {
    if (accountProfile) {
      setAccountProfile({
        ...accountProfile,
        profile_picture_url: newUrl,
      });
    }
    if (newUrl) {
      setSuccessMessage('Photo de profil mise à jour');
    } else {
      setSuccessMessage('Photo de profil supprimée');
    }
  }, [accountProfile]);

  if (!user) {
    return null;
  }

  if (isLoadingProfile) {
    return (
      <div className="profile-page">
        <Link to="/" className="profile-back-link">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Retour
        </Link>
        <div className="profile-loading">
          <span className="profile-button-spinner" style={{ width: 32, height: 32 }} />
          <p>Chargement du profil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <Link to="/" className="profile-back-link">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Retour
      </Link>

      <header className="profile-header">
        <h1 className="profile-title">Mon profil</h1>
        <p className="profile-subtitle">Gérez vos informations personnelles</p>
      </header>

      {/* Success message */}
      {successMessage && (
        <div className="profile-alert success" role="status">
          <svg className="profile-alert-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
          <p className="profile-alert-message">{successMessage}</p>
        </div>
      )}

      {/* Main profile card */}
      <div className="profile-card">
        {/* Profile picture and basic info */}
        <div className="profile-main-section">
          <ProfilePictureUpload
            currentPictureUrl={profilePictureUrl}
            initial={initial}
            backgroundColor={backgroundColor}
            onPictureUpdated={handlePictureUpdated}
          />

          <div className="profile-main-info">
            <div className="profile-name-row">
              <h2 className="profile-name">{displayName}</h2>
              <div className={`profile-verification ${isVerified ? 'verified' : 'unverified'}`}>
                {isVerified ? (
                  <>
                    <svg className="profile-verification-icon" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                    Vérifié
                  </>
                ) : (
                  <>
                    <svg className="profile-verification-icon" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                    </svg>
                    Non vérifié
                  </>
                )}
              </div>
            </div>
            <div className="profile-info-row">
              <div className="profile-info-left">
                <p className="profile-email-display">{email}</p>
                {company && <p className="profile-company">{company}</p>}
              </div>
              <div className="profile-info-right">
                <span className={`profile-role ${isAdmin ? 'admin' : 'member'}`}>
                  {isAdmin ? 'Administrateur' : 'Membre'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Email verification section for unverified users */}
        {!isVerified && (
          <div className="profile-section profile-verification-section">
            <h3 className="profile-section-title">Vérification de l'email</h3>

            {verificationSent && (
              <div className="profile-alert success">
                <svg className="profile-alert-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
                <p className="profile-alert-message">
                  Email de vérification envoyé ! Vérifiez votre boîte de réception.
                </p>
              </div>
            )}

            {verificationError && (
              <div className="profile-alert error">
                <svg className="profile-alert-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
                <p className="profile-alert-message">{verificationError}</p>
              </div>
            )}

            <p className="profile-section-description">
              Votre email n'est pas encore vérifié. Cliquez ci-dessous pour recevoir un nouveau lien de vérification.
            </p>

            <button
              type="button"
              className="profile-button profile-button-secondary"
              onClick={handleResendVerification}
              disabled={isResendLoading}
            >
              {isResendLoading ? (
                <>
                  <span className="profile-button-spinner" style={{ borderColor: 'rgba(55, 65, 81, 0.3)', borderTopColor: '#374151' }} />
                  Envoi...
                </>
              ) : (
                'Renvoyer l\'email de vérification'
              )}
            </button>
          </div>
        )}

        {/* Editable account info */}
        <div className="profile-section">
          <h3 className="profile-section-title">Informations du compte</h3>

          {/* OAuth notice */}
          {isOAuthUser && (
            <div className="profile-oauth-notice">
              <svg className="profile-oauth-notice-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
              </svg>
              <p>
                Votre compte est connecté via un fournisseur externe.
                Certaines informations ne peuvent pas être modifiées ici.
              </p>
            </div>
          )}

          {editError && (
            <div className="profile-alert error">
              <svg className="profile-alert-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
              <p className="profile-alert-message">{editError}</p>
            </div>
          )}

          <div className="profile-form">
            <div className="profile-form-row">
              <div className="profile-field">
                <label className="profile-label" htmlFor="profile-first-name">Prénom</label>
                <input
                  id="profile-first-name"
                  type="text"
                  className="profile-input"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="Votre prénom"
                />
              </div>

              <div className="profile-field">
                <label className="profile-label" htmlFor="profile-last-name">Nom</label>
                <input
                  id="profile-last-name"
                  type="text"
                  className="profile-input"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Votre nom"
                />
              </div>
            </div>

            <div className="profile-field">
              <label className="profile-label" htmlFor="profile-email">
                Email
                {!canChangeEmail && (
                  <svg className="profile-label-lock" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                  </svg>
                )}
              </label>
              <input
                id="profile-email"
                type="email"
                className="profile-input"
                value={editForm.email}
                onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="votre@email.com"
                disabled={!canChangeEmail}
              />
            </div>

            {company && (
              <div className="profile-field">
                <label className="profile-label" htmlFor="profile-company">Entreprise</label>
                <input
                  id="profile-company"
                  type="text"
                  className="profile-input"
                  value={company}
                  disabled
                />
              </div>
            )}

            <div className="profile-field">
              <label className="profile-label" htmlFor="profile-role">Rôle</label>
              <input
                id="profile-role"
                type="text"
                className="profile-input"
                value={isAdmin ? 'Administrateur' : 'Utilisateur'}
                disabled
              />
            </div>

            {/* Save/Cancel buttons - only visible when changes are made */}
            {hasChanges && (
              <div className="profile-form-actions">
                <button
                  type="button"
                  className="profile-button profile-button-secondary"
                  onClick={handleCancelChanges}
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="profile-button profile-button-primary"
                  onClick={handleSaveChanges}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="profile-button-spinner" />
                      Enregistrement...
                    </>
                  ) : (
                    'Enregistrer les modifications'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Danger zone */}
      <div className="profile-card profile-card-danger">
        <div className="profile-danger-header">
          <svg className="profile-danger-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
          </svg>
          <h3 className="profile-section-title" style={{ margin: 0 }}>Zone de danger</h3>
        </div>

        <p className="profile-danger-description">
          Supprimer mon compte
        </p>
        <p className="profile-danger-warning">
          Cette action est irréversible. Toutes vos données seront définitivement supprimées, y compris vos conversations, analyses et paramètres.
        </p>

        <button
          type="button"
          className="profile-button profile-button-danger"
          onClick={openDeleteModal}
        >
          Supprimer mon compte
        </button>
      </div>

      {/* Footer actions */}
      <div className="profile-footer">
        <button
          type="button"
          className="profile-button profile-button-secondary"
          onClick={handleLogout}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Se déconnecter
        </button>
      </div>

      {/* Delete Account Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleDeleteAccount}
        onCancel={closeDeleteModal}
        title="Supprimer le compte"
        message="Cette action est irréversible. Toutes vos données seront définitivement supprimées. Êtes-vous sûr de vouloir supprimer votre compte ?"
        confirmText="Supprimer définitivement"
        cancelText="Annuler"
        isLoading={isDeleting}
        error={deleteError}
      />
    </div>
  );
};
